import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Grid,
  Select,
  MenuItem,
} from '@mui/material';
import moment from 'moment-timezone';
import Sidebar from '../../components/Sidebar/Sidebar';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { searchTicketsByDate, searchRefundsByDate } from '../../utils/apiUtils';
import './TicketTracking.scss';

const TicketTracking = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [totals, setTotals] = useState(null); // New state to hold the totals
  const [page, setPage] = useState(0);
  const rowsPerPage = 15;

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  };

  useEffect(() => {
    const fetchTicketsAndRefundsByDate = async () => {
      if (startDate && endDate) {
        try {
          const startInParis = moment.utc(startDate).tz('Europe/Paris').startOf('day').toISOString();
          const endInParis = moment.utc(endDate).tz('Europe/Paris').endOf('day').toISOString();

          let ticketsData = await searchTicketsByDate(startInParis, endInParis);
          let refundsData = await searchRefundsByDate(startInParis, endInParis);

          let mergedData = ticketsData.map((ticket) => {
            let matchingRefund = refundsData.find((refund) => refund.ticket_id === ticket.ticket_id);
            let mergedTicket = { ...ticket };
            if (matchingRefund) {
              for (let key in matchingRefund) {
                if (!(key in ticket)) {
                  mergedTicket[key] = matchingRefund[key];
                }
              }
            }
            return mergedTicket;
          });

          let dates = mergedData.map((ticket) =>
            moment.utc(ticket.ticket_created).tz('Europe/Paris').startOf('day').toDate()
          );
          dates = [...new Set(dates.map((date) => +date))].map((date) => new Date(date));
          dates.sort((a, b) => a - b);

          let dailyData = dates.map((date) => {
            let ticketsOnDate = mergedData.filter(
              (ticket) => +moment.utc(ticket.ticket_created).tz('Europe/Paris').startOf('day').toDate() === +date
            );

            let Billing = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'billing').length; // old billing category
            let Ticket = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'ticket').length; // new general ticket category
            let Other = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'other').length; // new other category
            let SRQ = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'srq').length;
            let Refund = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'refund').length;
            let Tech = ticketsOnDate.filter((ticket) => ticket.ticket_category === 'tech').length;
            let RefundSupportNeeded = ticketsOnDate.filter(
              (ticket) => ticket.ticket_category === 'refund' && ticket.ticket_claimed != null
            ).length;
            let RefundNoSupportNeeded = ticketsOnDate.filter(
              (ticket) => ticket.ticket_category === 'refund' && ticket.ticket_claimed == null
            ).length;
            let Total = ticketsOnDate.length;

            return {
              date: moment(date).format('DD-MM-YYYY'),
              Billing: Billing,
              Ticket: Ticket, // new general ticket category
              Other: Other, // new other category
              SRQ: SRQ,
              Refund: Refund,
              Tech: Tech,
              RefundSupportNeeded: RefundSupportNeeded,
              RefundNoSupportNeeded: RefundNoSupportNeeded,
              Total: Total,
            };
          });

          // Calculate the totals
          const columnKeys = [
            'Billing',
            'Ticket', // general tickets count
            'Other', // new other category count
            'SRQ',
            'Refund',
            'Tech',
            'RefundSupportNeeded',
            'RefundNoSupportNeeded',
            'Total',
          ];
          const totals = columnKeys.reduce((acc, columnKey) => {
            acc[columnKey] = dailyData.reduce((sum, data) => sum + data[columnKey], 0);
            return acc;
          }, {});

          setDailyData(dailyData);
          setTickets(mergedData);
          setTotals(totals); // Set the totals state
        } catch (error) {
          console.error('Error fetching tickets:', error);
        }
      }
    };

    fetchTicketsAndRefundsByDate();
  }, [startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const filteredTickets = selectedAgent ? tickets.filter((ticket) => ticket.ticket_owner_name === selectedAgent) : tickets;

  return (
    <Grid container justifyContent="center">
      <Sidebar />

      <Grid item xs={10}>
        <Grid container justifyContent="center">
          <Typography variant="h2" gutterBottom>
            Ticket Tracking
          </Typography>
        </Grid>
        <Grid container justifyContent="center">
          <Typography variant="body1">UTC calculated to Paris Timezone.</Typography>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className="StyledButtonContainer">
              <Box className="StyledCard">
                <Typography variant="h5">Date Range</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              </Box>
              <Box className="StyledCard" style={{ height: '300px' }}>
                <Typography variant="h5">Select Agent</Typography>
                <Select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} className="SelectDropdown">
                  <MenuItem value="">Everyone</MenuItem>
                  {Array.from(new Set(tickets.map((ticket) => ticket.ticket_owner_name)))
                    .filter((agent) => agent)
                    .map((agent) => (
                      <MenuItem key={agent} value={agent}>
                        {agent}
                      </MenuItem>
                    ))}
                </Select>
              </Box>


              {/* <Box className="StyledCard" style={{ height: '300px' }}>
                <Typography variant="h5">Tickets Claimed</Typography>
                <Typography variant="body2" className="count">
                  {filteredTickets.filter((ticket) => ticket.ticket_claimed !== null).length}
                </Typography>
              </Box>
              <Box className="StyledCard">
                <Typography variant="h5">Tickets Closed</Typography>
                <Typography variant="body2" className="count">
                  {filteredTickets.filter((ticket) => ticket.ticket_closed !== null).length}
                </Typography>
              </Box>
              <Box className="StyledCard">
                <Typography variant="h5">Tickets Created</Typography>
                <Typography variant="body2" className="count">
                  {filteredTickets.length}
                </Typography>
              </Box> */}

              
            </Box>
          </Grid>
        </Grid>

        <Box mt={4}>
          <Typography variant="h5">Daily Ticket Statistics</Typography>
          <TableContainer component={Paper} className="customTable">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Other</TableCell>
                  <TableCell>SRQ</TableCell>
                  <TableCell>Tech</TableCell>
                  <TableCell>Refund</TableCell>
                  <TableCell>Refund Support Needed</TableCell>
                  <TableCell>Refund No Support Needed</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyData.map((data) => (
                  <TableRow key={data.date}>
                    <TableCell>{data.date}</TableCell>
                    <TableCell>{data.Billing}</TableCell>
                    <TableCell>{data.Ticket}</TableCell>
                    <TableCell>{data.Other}</TableCell>
                    <TableCell>{data.SRQ}</TableCell>
                    <TableCell>{data.Tech}</TableCell>
                    <TableCell>{data.Refund}</TableCell>
                    <TableCell>{data.RefundSupportNeeded}</TableCell>
                    <TableCell>{data.RefundNoSupportNeeded}</TableCell>
                    <TableCell>{data.Total}</TableCell>
                  </TableRow>
                ))}

                {/* Add a row for the totals */}
                {totals && (
                  <TableRow>
                    <TableCell>Total</TableCell>
                    <TableCell>{totals.Billing}</TableCell>
                    <TableCell>{totals.Ticket}</TableCell>
                    <TableCell>{totals.Other}</TableCell>
                    <TableCell>{totals.SRQ}</TableCell>
                    <TableCell>{totals.Tech}</TableCell>
                    <TableCell>{totals.Refund}</TableCell>
                    <TableCell>{totals.RefundSupportNeeded}</TableCell>
                    <TableCell>{totals.RefundNoSupportNeeded}</TableCell>
                    <TableCell>{totals.Total}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box mt={4}>
          <Typography variant="h5">Filtered Tickets</Typography>
          <TableContainer component={Paper} className="customTable">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket Owner</TableCell>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Ticket Name</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Closed At</TableCell>
                  <TableCell>Ticket Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ticket) => (
                  <TableRow key={ticket.ticket_id}>
                    <TableCell>{ticket.ticket_owner_name}</TableCell>
                    <TableCell>{ticket.ticket_id}</TableCell>
                    <TableCell>{ticket.ticket_name}</TableCell>
                    <TableCell>{formatDate(ticket.ticket_created)}</TableCell>
                    <TableCell>{formatDate(ticket.ticket_closed)}</TableCell>
                    <TableCell>{ticket.ticket_category}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[15]}
              component="div"
              count={filteredTickets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          </TableContainer>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TicketTracking;
